import { useRef, type KeyboardEvent, type ReactNode } from "react";
import {
  NSW_ACT_SCHOOL_TYPES,
  ORDER_REGIONS,
  type NswActSchoolType,
  type OrderRegion,
} from "../lib/products";

interface RegionSelectorProps {
  region: OrderRegion;
  schoolType: NswActSchoolType;
  onRegionChange: (region: OrderRegion) => void;
  onSchoolTypeChange: (schoolType: NswActSchoolType) => void;
}

const REGION_LABELS: Record<OrderRegion, { label: string; detail?: string }> = {
  nsw_act: { label: "NSW & ACT" },
  vic: { label: "VIC" },
  other: { label: "Other States", detail: "(Qld, SA, WA, TAS, NT)" },
};

// Simplified state outlines — decorative only, the label carries the meaning.
const REGION_SHAPES: Record<OrderRegion, ReactNode> = {
  nsw_act: <path d="M3 6h13l1.6-1 3.4 1-1.4 5-2.6 5-2 4-3-1.6-3-.4-2.6-2L3 15z" />,
  vic: <path d="M3 7.5l4 .6 3 2.2 3-.4 3 1.4 3 .5 3 2.6-3.6 1.6-2.8 2.6-2.2-1.6-2 .6-1-1-3.2 1.6L3 17z" />,
  other: (
    <>
      <path d="M2.5 10.5 4 8.5l2.5-1L8 5.5l2-.5 1-1.5 2 .5 1 1.5 1.5.5.5-3 1-.5 1 2.5 1.5 2.5L21 10l.5 3-1.5 3-2 2-2.5.5L14 17l-1.5-.5-1-1.5H9l-2.5 1-2.5.5-1-2.5z" />
      <path d="M15.8 20h2.6l-1.1 2.2z" />
    </>
  ),
};

const SCHOOL_TYPE_LABELS: Record<NswActSchoolType, string> = {
  non_government: "Non-government school",
  government: "Government school",
};

function SchoolIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3v4M12 3.5h3.2v2H12" />
      <path d="M8 21V9.5l4-2.5 4 2.5V21" />
      <path d="M8 13H4v8M16 13h4v8M3 21h18" />
      <path d="M11 21v-3.5h2V21M10.5 11.5h3M10.5 14.5h3M5.8 16h.9M17.3 16h.9M5.8 18.5h.9M17.3 18.5h.9" />
    </svg>
  );
}

/**
 * Radio-group keyboard behaviour (WAI-ARIA): one tab stop on the checked radio;
 * arrow keys move to — and select — the neighbouring option, wrapping at the ends.
 */
function useRadioKeys<T extends string>(options: readonly T[], value: T, onChange: (next: T) => void) {
  const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;

    event.preventDefault();
    const next = options[(options.indexOf(value) + step + options.length) % options.length];
    onChange(next);
    refs.current[next]?.focus();
  }

  return {
    radioProps: (option: T) => ({
      type: "button" as const,
      role: "radio" as const,
      "aria-checked": option === value,
      tabIndex: option === value ? 0 : -1,
      onClick: () => onChange(option),
      onKeyDown,
      ref: (el: HTMLButtonElement | null) => {
        refs.current[option] = el;
      },
    }),
  };
}

/**
 * Region choice for the direct order page (/ only). It decides what is DISPLAYED;
 * it never edits the cart, launches PunchOut, or restricts which school may order.
 */
export function RegionSelector({ region, schoolType, onRegionChange, onSchoolTypeChange }: RegionSelectorProps) {
  const regionKeys = useRadioKeys(ORDER_REGIONS, region, onRegionChange);
  const schoolTypeKeys = useRadioKeys(NSW_ACT_SCHOOL_TYPES, schoolType, onSchoolTypeChange);

  return (
    <section className="region-selector" aria-label="Region">
      <h2 className="order-step-heading" id="region-heading">Select your region</h2>
      <div className="region-options" role="radiogroup" aria-labelledby="region-heading">
        {ORDER_REGIONS.map((option) => (
          <button
            key={option}
            className={`region-option ${option === region ? "region-option--selected" : ""}`}
            data-region={option}
            {...regionKeys.radioProps(option)}
          >
            <svg
              className="region-option-shape"
              width="34"
              height="34"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
            >
              {REGION_SHAPES[option]}
            </svg>
            <span className="region-option-text">
              <span className="region-option-label">{REGION_LABELS[option].label}</span>
              {REGION_LABELS[option].detail && (
                <span className="region-option-detail">{REGION_LABELS[option].detail}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      {region === "nsw_act" && (
        <div className="school-type-panel">
          <h2 className="order-step-heading" id="school-type-heading">NSW &amp; ACT school type</h2>
          <div className="school-type-options" role="radiogroup" aria-labelledby="school-type-heading">
            {NSW_ACT_SCHOOL_TYPES.map((option) => (
              <button
                key={option}
                className={`school-type-option ${option === schoolType ? "school-type-option--selected" : ""}`}
                data-school-type={option}
                {...schoolTypeKeys.radioProps(option)}
              >
                <span className="school-type-dot" aria-hidden="true" />
                <span className="school-type-icon">
                  <SchoolIcon />
                </span>
                <span className="school-type-text">
                  <span className="school-type-label">{SCHOOL_TYPE_LABELS[option]}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
